import React from 'react'
import {log} from '@/logger'

import {api} from '../services/api'
import {PHOTO_TAGS_GENERIC_ERROR} from '../services/api/photos/listPhotoPersonTags'
import {FetchStatus} from './useRecognitionAttempts'

interface Params {
  photoId: number
  clientId: number
  // Gate the fetch so it never fires while the owning modal is closed.
  enabled: boolean
}

export interface TaggedCamper {
  personId: number
  manual: boolean
  displayName: string
  // Who placed a manual tag, resolved to a display name. null on FR tags.
  taggedByName: string | null
}

export interface UsePhotoPersonTagsState {
  status: FetchStatus
  tags: TaggedCamper[]
  errorMessage: string | null
}

const fallbackName = (personId: number): string => `Person ${personId}`

/**
 * Read-only per-photo tag list for the Tagged Campers modal. Modelled on
 * useRecognitionAttempts: a single effect, a `cancelled` stale-guard, and a
 * follow-on name-resolution stage against the same `getNames` batch wrapper.
 *
 * Name resolution is a display enhancement: a failure falls back to
 * `Person <id>` per unresolved id and still reports 'resolved' — a tag list we
 * have is worth showing without names, the inverse is not true.
 *
 * A tag-list load failure never blanks an existing `tags` array (PR 10143
 * review carry-forward): the failure branch simply does not call `setTags`.
 * What keeps a failure from *reading* as "no tags" is the consumer's separate
 * 'rejected' branch — PhotoTagModal renders `tags-error`, never the empty-state
 * copy — so the two are never confusable regardless of what `tags` holds.
 */
export const usePhotoPersonTags = ({
  photoId,
  clientId,
  enabled,
}: Params): UsePhotoPersonTagsState => {
  const [status, setStatus] = React.useState<FetchStatus>('idle')
  const [tags, setTags] = React.useState<TaggedCamper[]>([])
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    setStatus('pending')
    setErrorMessage(null)

    const load = async (): Promise<void> => {
      const response = await api.photos.listPhotoPersonTags({
        clientId,
        photoId,
      })

      if (cancelled) return

      if (response.result !== 'success') {
        // Deliberately NOT `response.error.message`, unlike the sibling
        // useRecognitionAttempts/usePersonTaggingDiagnostics hooks: handleError
        // swaps in the server's own message for a 4xx, and this view must stay
        // generic (AB#23610 plan, D3).
        setErrorMessage(PHOTO_TAGS_GENERIC_ERROR)
        setStatus('rejected')
        // `error.path`, not `error.message`: handleError substitutes the
        // SERVER's `response.data.message` for any 4xx, so `message` is
        // upstream-controlled text. Breadcrumbs go to Bugsnag (a third-party
        // sink), so only code-controlled values are sent.
        log.breadcrumb('Failed to load photo person tags', {
          photoId,
          errorPath: response.error.path,
        })
        return
      }

      const rawTags = response.data

      if (rawTags.length === 0) {
        setTags([])
        setStatus('resolved')
        return
      }

      // One deduplicated getNames call covering both the tagged campers and
      // their taggers — an id appearing as both is requested once.
      const ids = Array.from(
        new Set(
          rawTags.flatMap((tag) =>
            tag.taggingPersonId !== null
              ? [tag.personId, tag.taggingPersonId]
              : [tag.personId],
          ),
        ),
      )

      const namesResponse = await api.persons.getNames({
        clientID: clientId,
        ids,
      })

      if (cancelled) return

      if (namesResponse.result !== 'success') {
        log.breadcrumb('Failed to resolve photo tag names', {photoId})
      }

      const nameMap =
        namesResponse.result === 'success'
          ? namesResponse.data
          : new Map<number, string>()

      setTags(
        rawTags.map((tag) => ({
          personId: tag.personId,
          manual: tag.manual,
          // `||`, not `??`: getNames can resolve a person with no name on
          // file to an empty string, which must still fall back rather than
          // render a blank row (matches resolveCandidateName/
          // resolveTaggerName's fallback semantics elsewhere in this area).
          displayName: nameMap.get(tag.personId) || fallbackName(tag.personId),
          taggedByName:
            tag.taggingPersonId !== null
              ? nameMap.get(tag.taggingPersonId) ||
                fallbackName(tag.taggingPersonId)
              : null,
        })),
      )
      setStatus('resolved')
    }

    load()

    return (): void => {
      cancelled = true
    }
  }, [enabled, clientId, photoId])

  return {status, tags, errorMessage}
}
